import * as Switch from '@radix-ui/react-switch'

/**
 * The small controls every missing-readings view shares.
 *
 * Each view used to carry its own copy, and the copies drifted: the dashboard
 * card and the device panel kept offering 7/30/90 days after the day pages had
 * moved to 7 through 365. One definition here is what keeps them in step.
 */

/** Selectable windows, matching MISSING_READINGS_RANGE_OPTIONS in the API. */
export const RANGE_OPTIONS = [7, 15, 30, 90, 180, 365] as const

/** Device-scoped ceiling — the API's parseHistoricalDay. */
export const MISSING_READINGS_MAX_DAYS = 365

/**
 * Fleet-scoped ceiling, lower than the device one — the API clamps a fleet
 * request above it (MISSING_READINGS_FLEET_MAX_DAYS in constants/sigfox.ts), so
 * a fleet view must not offer a window it will not get.
 */
export const MISSING_READINGS_FLEET_MAX_DAYS = 180

/**
 * Segmented day-window picker, offering only windows up to `maxDays`.
 * `options` replaces the missing-readings windows for a view that also has
 * to suit another query (the device Connectivity card).
 */
export function RangeToggle({
  value,
  onChange,
  maxDays = MISSING_READINGS_MAX_DAYS,
  options = RANGE_OPTIONS,
}: {
  value: number
  onChange: (days: number) => void
  maxDays?: number
  options?: readonly number[]
}) {
  return (
    <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs">
      {options.filter((d) => d <= maxDays).map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          aria-pressed={value === d}
          className={`px-2.5 py-1 font-medium ${
            value === d ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {d}d
        </button>
      ))}
    </div>
  )
}

/** The compact labelled on/off switch used for the views' display filters. */
export function LabeledSwitch({
  checked,
  onCheckedChange,
  label,
  title,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  label: string
  title?: string
}) {
  return (
    <label
      className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none flex-shrink-0"
      title={title}
    >
      <Switch.Root
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="relative h-4 w-7 rounded-full bg-gray-200 transition-colors data-[state=checked]:bg-blue-600"
      >
        <Switch.Thumb className="block h-3 w-3 translate-x-0.5 rounded-full bg-white transition-transform data-[state=checked]:translate-x-[0.875rem]" />
      </Switch.Root>
      {label}
    </label>
  )
}

/**
 * The amber asterisk marking a day with a reconfiguration, where the interval
 * estimate is withheld. Always carries its explanation as a tooltip.
 */
export function ReconfiguredMark({ title }: { title: string }) {
  return (
    <span className="ml-1 text-amber-500" title={title}>
      *
    </span>
  )
}
